/**
 * Barra de triage: el veredicto y los conteos por severidad que van arriba de
 * todo. Es la tesis del tablero de riesgo — el panel se organiza por urgencia y
 * lo primero que se ve es cuántos frentes están rotos.
 *
 * Puro: los tres roles arman su lista de entradas y esta función decide el estado
 * general y el titular. Sin esto, cada pantalla elegiría su propio criterio de
 * "cuál es el peor" y en algún lado no coincidirían.
 */
import type { Health } from '@ops/contracts'
import type { TriageConteo } from '../../ui'

import { formatMetricValue } from '../../lib/format'
import { peorSeveridad } from './frentes'

export interface EntradaTriage {
  severidad: Health
  cantidad: number
  /** Qué se cuenta, en plural, ej. "frentes rotos". */
  label: string
  /** Singular, para cuando la cantidad es 1. */
  labelSingular?: string
  onClick?: () => void
}

export interface Triage {
  estado: Health
  titular: string
  conteos: TriageConteo[]
}

const PRIORIDAD: readonly Health[] = ['critical', 'warning', 'unknown', 'ok']

/**
 * @param entradas Conteos del rol, en el orden en que se muestran.
 * @param titularVacio Qué decir cuando no hay nada que triar.
 */
export function tableroDeTriage(
  entradas: readonly EntradaTriage[],
  titularVacio: string,
): Triage {
  const conAlgo = entradas.filter((e) => e.cantidad > 0)
  const estado = peorSeveridad(conAlgo.map((e) => e.severidad))

  // El titular lo dicta la entrada más grave con cantidad, no la primera de la
  // lista: si hay un crítico, el titular habla del crítico.
  const protagonista =
    PRIORIDAD.map((sev) => conAlgo.find((e) => e.severidad === sev)).find(
      (e) => e !== undefined,
    ) ?? null

  const titular =
    protagonista === null
      ? titularVacio
      : `${formatMetricValue(protagonista.cantidad, 'count')} ${etiqueta(protagonista)}`

  return {
    estado: conAlgo.length === 0 ? 'ok' : estado,
    titular,
    conteos: entradas.map((e) => ({
      severidad: e.severidad,
      cantidad: e.cantidad,
      label: etiqueta(e),
      onClick: e.onClick,
    })),
  }
}

function etiqueta(entrada: EntradaTriage): string {
  return entrada.cantidad === 1
    ? (entrada.labelSingular ?? entrada.label)
    : entrada.label
}
